import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Descriptions, Form, Input, InputNumber,
  Modal, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, CloseOutlined, EditOutlined, SaveOutlined, SyncOutlined } from '@ant-design/icons';
import API_BASE_URL, { ENDPOINTS } from '../../helpers/api/apiConfig';
import { COMMAND_CATALOGUE } from '../Operations/missionCommandCatalogue';
import ModuleFormList from './ModuleFormList';
import type {
  DeviceCapabilityDefinitionItem, DeviceModuleItem, DeviceTypeCapabilityItem,
  ModuleDefinitionItem, DeviceTypeItem, VehicleCapabilities, VehicleProfileItem,
} from './types';

const { Text, Title } = Typography;

const capabilityCategoryLabels: Record<string, string> = {
  navigation: 'Navigation',
  mapping: 'Mapping',
  autonomy: 'Autonomy',
  localization: 'Localization',
  safety: 'Safety',
  payload: 'Payload',
  connectivity: 'Connectivity',
  other: 'Other',
};
const capabilityCategoryColors: Record<string, string> = {
  navigation: 'blue',
  mapping: 'purple',
  autonomy: 'cyan',
  localization: 'geekblue',
  safety: 'gold',
  payload: 'volcano',
  connectivity: 'green',
  other: 'default',
};

interface DeviceTypeDetailProps {
  deviceTypeId: number;
  onBack: () => void;
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
  onSaved?: () => void;
}

const stringifyJson = (value: unknown) => {
  if (value === undefined || value === null) return '';
  try { return JSON.stringify(value, null, 2); } catch { return ''; }
};

const parseJsonObjectText = (value: unknown, label: string, optional = false): Record<string, any> | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) { if (optional) return null; throw new Error(`${label} is required`); }
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error('invalid');
    return parsed;
  } catch { throw new Error(`${label} must be a valid JSON object`); }
};

const normalizeOptionalField = (v: unknown): string | null => {
  const n = typeof v === 'string' ? v.trim() : '';
  return n || null;
};

const DeviceTypeDetail: React.FC<DeviceTypeDetailProps> = ({
  deviceTypeId, onBack, buildAuthHeaders, onAuthMismatch, onSaved,
}) => {
  const [deviceType, setDeviceType] = useState<DeviceTypeItem | null>(null);
  const [moduleDefinitions, setModuleDefinitions] = useState<ModuleDefinitionItem[]>([]);
  const [capabilityDefinitions, setCapabilityDefinitions] = useState<DeviceCapabilityDefinitionItem[]>([]);
  const [vehicleProfiles, setVehicleProfiles] = useState<VehicleProfileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [typeForm] = Form.useForm();

  const fetchDeviceType = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/device-types/${deviceTypeId}`, {
        headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setDeviceType(data?.data ?? data ?? null);
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.message || 'Failed to load device type');
      }
    } catch (e) {
      console.error(e);
      message.error('Failed to load device type');
    } finally { setLoading(false); }
  }, [buildAuthHeaders, deviceTypeId, onAuthMismatch]);

  const fetchModuleDefs = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.MODULE_DEFINITIONS, {
        headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setModuleDefinitions(Array.isArray(data?.data) ? data.data : []);
      }
    } catch (e) { console.error(e); }
  }, [buildAuthHeaders]);

  const fetchCapabilityDefs = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.DEVICE_CAPABILITY_DEFINITIONS, {
        headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setCapabilityDefinitions(Array.isArray(data?.data) ? data.data : []);
      }
    } catch (e) { console.error(e); }
  }, [buildAuthHeaders]);

  const fetchVehicleProfiles = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.VEHICLE_PROFILES, {
        headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setVehicleProfiles(Array.isArray(data?.data) ? data.data : []);
      }
    } catch (e) { console.error(e); }
  }, [buildAuthHeaders]);

  useEffect(() => { fetchDeviceType(); fetchModuleDefs(); fetchCapabilityDefs(); fetchVehicleProfiles(); }, [fetchDeviceType, fetchModuleDefs, fetchCapabilityDefs, fetchVehicleProfiles]);

  const vehicleProfileOptions = useMemo(() =>
    vehicleProfiles.map((vp) => ({
      value: vp.id,
      label: `${vp.name} (${vp.vehicleClass})`,
    })),
  [vehicleProfiles]);

  const capabilitySelectOptions = useMemo(() => {
    const byCategory = new Map<string, DeviceCapabilityDefinitionItem[]>();
    capabilityDefinitions.forEach((definition) => {
      const category = definition.category || 'other';
      byCategory.set(category, [...(byCategory.get(category) || []), definition]);
    });
    return Array.from(byCategory.entries())
      .sort(([left], [right]) => (capabilityCategoryLabels[left] || left).localeCompare(capabilityCategoryLabels[right] || right))
      .map(([category, definitions]) => ({
        label: capabilityCategoryLabels[category] || category,
        options: definitions
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name))
          .map((definition) => ({ value: definition.id, label: definition.name })),
      }));
  }, [capabilityDefinitions]);

  const capabilityIdsFromRows = useCallback((rows?: DeviceTypeCapabilityItem[] | null) => (
    (rows || [])
      .filter((row) => row.enabled !== false)
      .map((row) => row.capabilityDefinitionId || row.definition?.id || row.capabilityDefinition?.id)
      .filter((id): id is number => typeof id === 'number')
  ), []);

  const buildCapabilityPayload = useCallback((ids: number[] = []) => (
    ids.map((definitionId, index) => ({
      capabilityDefinitionId: definitionId,
      enabled: true,
      source: 'device_type',
      sortOrder: index,
    }))
  ), []);

  const renderCapabilityTags = useCallback((capabilities?: DeviceTypeCapabilityItem[] | null) => {
    const enabled = (capabilities || []).filter((capability) => capability.enabled !== false);
    if (enabled.length === 0) return <Text type="secondary">None</Text>;
    return (
      <Space wrap size={[4, 4]}>
        {enabled.map((capability) => {
          const definition = capability.definition || capability.capabilityDefinition || null;
          const key = capability.key || definition?.key || String(capability.capabilityDefinitionId || capability.id || capability.name);
          const category = capability.category || definition?.category || 'other';
          return (
            <Tag key={key} color={capabilityCategoryColors[category] || 'default'}>
              {capability.name || definition?.name || key}
            </Tag>
          );
        })}
      </Space>
    );
  }, []);

  const startEditing = useCallback(() => {
    if (!deviceType) return;
    const p = deviceType.vehicleCapabilities;
    typeForm.setFieldsValue({
      name: deviceType.name,
      description: deviceType.description ?? undefined,
      commandProtocol: deviceType.commandProtocol ?? 'oscar',
      moduleTemplates: (deviceType.moduleTemplates || []).map((c) => ({
        moduleDefinitionId: c.moduleDefinitionId ?? undefined,
        name: c.name ?? undefined,
        instanceKey: c.instanceKey ?? undefined,
        isDefault: c.isDefault ?? true,
        config: c.config || {},
        configText: stringifyJson(c.config),
        metadataText: stringifyJson(c.metadata),
      })),
      vehicleProfileId: deviceType.vehicleProfileId ?? undefined,
      capabilityIds: capabilityIdsFromRows(deviceType.capabilities),
      defaultAltitudeMode: p?.defaultAltitudeMode ?? undefined,
      defaultCruiseSpeed: p?.defaultCruiseSpeed ?? undefined,
      defaultTurnRadiusMeters: p?.defaultTurnRadiusMeters ?? undefined,
      defaultHoverSpeed: p?.defaultHoverSpeed ?? undefined,
      availableCommands: Array.isArray(p?.availableCommands) ? p!.availableCommands : [],
    });
    setEditing(true);
  }, [capabilityIdsFromRows, deviceType, typeForm]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    typeForm.resetFields();
  }, [typeForm]);

  const saveDeviceType = useCallback(async () => {
    try {
      const values = await typeForm.validateFields();
      const nextTemplates = Array.isArray(values.moduleTemplates)
        ? values.moduleTemplates.map((item: any, i: number) => ({
            moduleDefinitionId: item?.moduleDefinitionId ?? null,
            name: normalizeOptionalField(item?.name),
            instanceKey: normalizeOptionalField(item?.instanceKey),
            isDefault: item?.isDefault ?? true,
            config: item?.config && typeof item.config === 'object' && !Array.isArray(item.config)
              ? item.config
              : parseJsonObjectText(item?.configText, `Module template ${i + 1} config`, true),
            metadata: parseJsonObjectText(item?.metadataText, `Module template ${i + 1} metadata`, true),
          }))
        : [];

      const dupes = new Set<string>();
      nextTemplates.forEach((c: any) => {
        const k = String(c.instanceKey || c.moduleDefinitionId).toLowerCase();
        if (dupes.has(k)) throw new Error(`Module template "${k}" is duplicated`);
        dupes.add(k);
      });

      const hasMission = values.defaultAltitudeMode
        || values.defaultCruiseSpeed || values.defaultHoverSpeed
        || typeof values.defaultTurnRadiusMeters === 'number'
        || (Array.isArray(values.availableCommands) && values.availableCommands.length > 0);

      const vehicleCapabilities: VehicleCapabilities | null = hasMission ? {
        defaultAltitudeMode: normalizeOptionalField(values.defaultAltitudeMode),
        defaultCruiseSpeed: typeof values.defaultCruiseSpeed === 'number' ? values.defaultCruiseSpeed : null,
        defaultTurnRadiusMeters: typeof values.defaultTurnRadiusMeters === 'number' ? values.defaultTurnRadiusMeters : null,
        defaultHoverSpeed: typeof values.defaultHoverSpeed === 'number' ? values.defaultHoverSpeed : null,
        availableCommands: Array.isArray(values.availableCommands) && values.availableCommands.length > 0
          ? values.availableCommands : null,
      } : null;

      setSaving(true);
      const capabilities = buildCapabilityPayload(Array.isArray(values.capabilityIds) ? values.capabilityIds : []);
      const res = await fetch(`${API_BASE_URL}/device-types/${deviceTypeId}`, {
        method: 'PUT', headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify({
          name: String(values.name || '').trim(),
          description: normalizeOptionalField(values.description),
          commandProtocol: values.commandProtocol || null,
          moduleTemplates: nextTemplates,
          capabilities,
          vehicleCapabilities,
          vehicleProfileId: typeof values.vehicleProfileId === 'number' ? values.vehicleProfileId : null,
        }),
      });

      if (res.ok) {
        message.success('Device type updated');
        setEditing(false);
        await fetchDeviceType();
        onSaved?.();
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.message || 'Failed to save');
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  }, [buildAuthHeaders, buildCapabilityPayload, deviceTypeId, fetchDeviceType, onAuthMismatch, onSaved, typeForm]);

  const syncTemplatesToDevices = useCallback(async (mode: 'missing_only' | 'replace_all') => {
    if (!deviceType) return;

    const run = async () => {
      try {
        setSyncing(true);
        const res = await fetch(`${API_BASE_URL}/device-types/${deviceType.id}/sync-modules`, {
          method: 'POST',
          headers: buildAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({ mode }),
        });
        const data = await res.json();
        if (res.ok) {
          const result = data?.data || {};
          message.success(`Synced ${result.createdModules || 0} module${result.createdModules === 1 ? '' : 's'} across ${result.deviceCount || 0} device${result.deviceCount === 1 ? '' : 's'}`);
          await fetchDeviceType();
          onSaved?.();
        } else {
          if (await onAuthMismatch(res, data)) return;
          message.error(data?.message || 'Failed to sync templates to devices');
        }
      } catch (error: any) {
        message.error(error?.message || 'Failed to sync templates to devices');
      } finally {
        setSyncing(false);
      }
    };

    if (mode === 'replace_all') {
      Modal.confirm({
        title: 'Replace physical modules on existing devices?',
        content: 'This removes each device module list for this type and recreates it from the current templates. Device-specific module configuration will be overwritten.',
        okText: 'Replace modules',
        okButtonProps: { danger: true },
        onOk: run,
      });
      return;
    }

    void run();
  }, [buildAuthHeaders, deviceType, fetchDeviceType, onAuthMismatch, onSaved]);

  const capColumns = useMemo<ColumnsType<DeviceModuleItem>>(() => [
    { title: 'Key', key: 'key', render: (_, r) => <Tag color="geekblue">{r.moduleDefinition?.key || r.moduleDefinitionId}</Tag> },
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v?: string | null) => v || <Text type="secondary">—</Text> },
    { title: 'Instance', dataIndex: 'instanceKey', key: 'instanceKey', render: (v?: string | null) => v || <Text type="secondary">—</Text> },
    { title: 'Default', dataIndex: 'isDefault', key: 'default', width: 80, render: (v?: boolean) => v !== false ? <Tag color="green">Yes</Tag> : <Tag>No</Tag> },
    { title: 'Config', key: 'config', render: (_, r) => r.config ? <Text code style={{ fontSize: 11 }}>{stringifyJson(r.config).slice(0, 80)}</Text> : <Text type="secondary">—</Text> },
  ], []);

  /* Derive show/hide from the selected VehicleProfile's vehicleClass */
  const watchedProfileId = Form.useWatch('vehicleProfileId', typeForm);
  const selectedProfile = vehicleProfiles.find((vp) => vp.id === watchedProfileId);
  const vehicleClass = selectedProfile?.vehicleClass ?? null;
  const showAltitude = !vehicleClass || vehicleClass !== 'ground';
  const showHoverSpeed = !vehicleClass || ['multirotor', 'vtol', 'submarine'].includes(vehicleClass);

  if (loading) return <Card loading style={{ marginTop: 16 }} />;
  if (!deviceType) return <Alert type="error" message="Device type not found" showIcon style={{ marginTop: 16 }} />;

  const profile = deviceType.vehicleCapabilities;

  /* ── EDITING MODE: inline form ── */
  if (editing) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={cancelEditing} type="link">Cancel editing</Button>
          <Space>
            <Button icon={<CloseOutlined />} onClick={cancelEditing}>Cancel</Button>
            <Button icon={<SaveOutlined />} type="primary" onClick={saveDeviceType} loading={saving}>Save changes</Button>
          </Space>
        </Space>

        <Card title={<Title level={4} style={{ margin: 0 }}>Editing: {deviceType.name}</Title>}>
          <Form form={typeForm} layout="vertical">
            <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }, { max: 100 }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Description" name="description" rules={[{ max: 1000 }]}>
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item
              label="Command protocol"
              name="commandProtocol"
              help="Controls how the mission command editor behaves for robots of this type. Oscar: simplified form, no MAVLink fields. MAVLink/ArduPilot: show command codes, frame and altitude. Custom: full form with raw JSON params editor."
            >
              <Select options={[
                { value: 'oscar', label: 'Oscar (default — simplified, no MAVLink fields)' },
                { value: 'mavlink', label: 'MAVLink (show command code, frame, altitude)' },
                { value: 'ardupilot', label: 'ArduPilot (MAVLink + filter to ArduPilot commands only)' },
                { value: 'custom', label: 'Custom (full form + raw JSON params editor)' },
              ]} />
            </Form.Item>
          </Form>
        </Card>

        <Card title="Vehicle Profile & Capabilities">
          <Form form={typeForm} layout="vertical">
            <Form.Item
              label="Vehicle profile"
              name="vehicleProfileId"
              help="Links this device type to a reusable vehicle platform profile (ground rover, multicopter, etc.). Determines fleet eligibility and orchestration capabilities."
            >
              <Select allowClear placeholder="None — stationary device" optionFilterProp="label" options={vehicleProfileOptions} />
            </Form.Item>
            {showAltitude && (
              <Form.Item label="Default altitude mode" name="defaultAltitudeMode">
                <Select allowClear placeholder="Not set" options={[
                  { value: 'relative', label: 'Relative to Home' },
                  { value: 'absolute', label: 'Absolute (AMSL)' },
                  { value: 'terrain', label: 'Terrain Following' },
                ]} />
              </Form.Item>
            )}

            <Space size="middle" style={{ width: '100%' }}>
              <Form.Item label="Cruise speed (m/s)" name="defaultCruiseSpeed" style={{ flex: 1 }}>
                <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
              {showHoverSpeed && (
                <Form.Item label="Hover speed (m/s)" name="defaultHoverSpeed" style={{ flex: 1 }}>
                  <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
                </Form.Item>
              )}
              <Form.Item label="Turn radius (m)" name="defaultTurnRadiusMeters" style={{ flex: 1 }}>
                <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Form.Item label="Supported commands" name="availableCommands">
              <Select mode="multiple" allowClear placeholder="All (no filter)" optionFilterProp="label"
                options={COMMAND_CATALOGUE.map((cmd) => ({ value: cmd.id, label: `${cmd.displayName} (${cmd.kind})` }))} />
            </Form.Item>
          </Form>
        </Card>

        <Card title="Capabilities">
          <Form form={typeForm} layout="vertical">
            <Form.Item label="Platform capabilities" name="capabilityIds" extra="Asset abilities used by mission eligibility. Modules remain triggerable components.">
              <Select mode="multiple" allowClear showSearch placeholder="Select capabilities" optionFilterProp="label" options={capabilitySelectOptions} />
            </Form.Item>
          </Form>
        </Card>

        <Card title="Module templates">
          <Form form={typeForm} layout="vertical">
            <ModuleFormList form={typeForm} moduleDefinitions={moduleDefinitions} name="moduleTemplates" title="template" />
          </Form>
        </Card>

        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={cancelEditing}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={saveDeviceType} loading={saving}>Save changes</Button>
        </Space>
      </Space>
    );
  }

  /* ── READ-ONLY MODE: view details ── */
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} type="link">Back to device types</Button>
        <Button icon={<EditOutlined />} type="primary" onClick={startEditing}>Edit</Button>
      </Space>

      <Card title={<Title level={4} style={{ margin: 0 }}>{deviceType.name}</Title>}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="ID">{deviceType.id}</Descriptions.Item>
          <Descriptions.Item label="Devices">{deviceType.deviceCount ?? 0}</Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>{deviceType.description || <Text type="secondary">None</Text>}</Descriptions.Item>
          <Descriptions.Item label="Vehicle profile" span={2}>
            {deviceType.vehicleProfile ? (
              <Tag color="cyan">{deviceType.vehicleProfile.name} ({deviceType.vehicleProfile.vehicleClass})</Tag>
            ) : <Text type="secondary">None — stationary device</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Command protocol" span={2}>
            {deviceType.commandProtocol ? (
              <Tag color={deviceType.commandProtocol === 'oscar' ? 'blue' : deviceType.commandProtocol === 'ardupilot' ? 'orange' : deviceType.commandProtocol === 'mavlink' ? 'geekblue' : 'purple'}>
                {deviceType.commandProtocol}
              </Tag>
            ) : <Text type="secondary">oscar (default)</Text>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="Module templates"
        size="small"
        extra={
          <Space size="small" wrap>
            <Button
              size="small"
              icon={<SyncOutlined />}
              loading={syncing}
              disabled={(deviceType.moduleTemplates || []).length === 0 || (deviceType.deviceCount || 0) === 0}
              onClick={() => syncTemplatesToDevices('missing_only')}
            >
              Add missing to devices
            </Button>
            <Button
              size="small"
              danger
              loading={syncing}
              disabled={(deviceType.moduleTemplates || []).length === 0 || (deviceType.deviceCount || 0) === 0}
              onClick={() => syncTemplatesToDevices('replace_all')}
            >
              Replace device modules
            </Button>
          </Space>
        }
      >
        {(deviceType.moduleTemplates || []).length === 0 ? (
          <Alert type="info" showIcon message="No module templates defined yet" description="Click Edit to add defaults from the catalogue." />
        ) : (
          <Table rowKey={(row) => String(row.id || row.instanceKey || row.moduleDefinitionId)} dataSource={deviceType.moduleTemplates} columns={capColumns} pagination={false} size="small" />
        )}
      </Card>

      <Card title="Capabilities" size="small">
        {(deviceType.capabilities || []).filter((capability) => capability.enabled !== false).length === 0 ? (
          <Alert type="info" showIcon message="No capabilities assigned yet" description="Click Edit to add platform abilities for mission eligibility." />
        ) : (
          renderCapabilityTags(deviceType.capabilities)
        )}
      </Card>

      {profile && (
        <Card title="Vehicle Capabilities" size="small">
          <Descriptions column={2} size="small" bordered>
            {profile.defaultAltitudeMode && <Descriptions.Item label="Altitude mode">{profile.defaultAltitudeMode}</Descriptions.Item>}
            {profile.defaultCruiseSpeed != null && <Descriptions.Item label="Cruise speed">{profile.defaultCruiseSpeed} m/s</Descriptions.Item>}
            {profile.defaultHoverSpeed != null && <Descriptions.Item label="Hover speed">{profile.defaultHoverSpeed} m/s</Descriptions.Item>}
            {profile.defaultTurnRadiusMeters != null && <Descriptions.Item label="Turn radius">{profile.defaultTurnRadiusMeters} m</Descriptions.Item>}
            {profile.availableCommands && profile.availableCommands.length > 0 && (
              <Descriptions.Item label="Supported commands" span={2}>
                <Space wrap size={[4, 4]}>{profile.availableCommands.map((cmd) => <Tag key={cmd}>{cmd}</Tag>)}</Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}
    </Space>
  );
};

export default DeviceTypeDetail;
